export class ToolRegistry {
  constructor() {
    this.tools = new Map();
    this.categories = new Set();
    this.eventListeners = new Map();
    this.hasExecuteTool = true;
    this.hasTools = true;
    this.type = "object";
  }

  register(tool, options = { skipDuplicates: false }) {
    if (
      !tool ||
      !tool.id ||
      !tool.execute ||
      typeof tool.execute !== "function"
    ) {
      throw new Error(
        `Invalid tool: missing id or execute method: ${JSON.stringify(tool?.id || "unknown")}`
      );
    }

    if (this.tools.has(tool.id) && !options.skipDuplicates) {
      throw new Error(`Tool with ID ${tool.id} already registered`);
    }

    this.tools.set(tool.id, tool);
    this.categories.add(tool.category);

    this.emit("toolRegistered", { tool });

    return this;
  }

  registerMany(tools, options = {}) {
    tools.forEach((tool) => this.register(tool, options));
    return this;
  }

  getTool(id, requireTool = true) {
    try {
      const tool = this.tools.get(id);

      if (!tool && requireTool) {
        throw new Error(`Tool ${id} not found`);
      }

      return tool || null;
    } catch (error) {
      if (requireTool) {
        throw error;
      }
      console.warn(`Error getting tool ${id}:`, error.message);
      return null;
    }
  }

  getToolsByCategory(category) {
    return Array.from(this.tools.values()).filter(
      (tool) => tool.category === category
    );
  }

  getCategories() {
    return Array.from(this.categories);
  }

  searchTools(query) {
    const searchRegex = new RegExp(query, "i");

    return Array.from(this.tools.values()).filter(
      (tool) =>
        searchRegex.test(tool.name) ||
        searchRegex.test(tool.description) ||
        searchRegex.test(tool.id)
    );
  }

  async executeTool(id, enabledToolNames = null, ...args) {
    try {
      // Validate tool authorization if enabled tools are provided
      if (enabledToolNames instanceof Set && enabledToolNames.size > 0) {
        if (!enabledToolNames.has(id)) {
          const enabledList = Array.from(enabledToolNames).join(", ");
          throw new Error(
            `Tool '${id}' is not authorized for this request. Enabled tools: ${enabledList || "none"}`
          );
        }
      }

      let tool = this.getTool(id, false);

      if (!tool) {
        try {
          await this.loadToolDynamically(id);
          tool = this.getTool(id, false);
        } catch (loadError) {
          console.error(`Failed to dynamically load tool ${id}:`, loadError);
        }
      }

      if (!tool) {
        throw new Error(`Tool ${id} not found after load attempt`);
      }

      try {
        this.emit("toolExecutionStart", { tool, args });

        // Log image generation tool execution
        if (id === "image_gen") {
          console.log("🔧 [ToolRegistry] Executing image_gen tool:", {
            toolId: id,
            toolName: tool.name,
            args: args?.[0] || {},
            timestamp: new Date().toISOString(),
          });
        }

        const result = await tool.execute(...args);

        // Log image generation tool completion
        if (id === "image_gen") {
          console.log("✅ [ToolRegistry] image_gen tool execution completed:", {
            toolId: id,
            hasResult: !!result,
            success: result?.success !== false,
            timestamp: new Date().toISOString(),
          });
        }

        this.emit("toolExecutionComplete", { tool, result });
        return result;
      } catch (error) {
        this.emit("toolExecutionError", { tool, error });
        throw error;
      }
    } catch (error) {
      this.emit("toolExecutionError", { id, error });
      throw error;
    }
  }

  async loadToolDynamically(id) {
    try {
      let config;
      try {
        config = await import("./config");
      } catch (configError) {
        console.warn(`Could not import config:`, configError.message);
        config = { TOOL_REGISTRY: {}, TOOL_CATEGORIES: {} };
      }

      if (config.TOOL_REGISTRY && config.TOOL_REGISTRY[id]) {
        const toolConfig = config.TOOL_REGISTRY[id];

        if (toolConfig) {
          try {
            this.register(toolConfig, { skipDuplicates: true });

            return toolConfig;
          } catch (registerError) {
            console.error(
              `Error registering ${id} from config:`,
              registerError.message
            );
          }
        }
      }

      const toolPaths = {
        web_search: "./Tools/web/webSearchTool",
        image_gen: "./Tools/image/ImageGenTool",
      };

      const importPath = toolPaths[id];
      if (!importPath) {
        if (config.TOOL_CATEGORIES) {
          for (const [category, data] of Object.entries(
            config.TOOL_CATEGORIES
          )) {
            if (data.tools && data.tools.includes(id)) {
              const inferredPath = `./Tools/${category.toLowerCase()}/${id}Tool`;

              try {
                const module = await import(/* @vite-ignore */ inferredPath);

                const hasRequiredToolProperties = (obj) => {
                  return (
                    obj &&
                    typeof obj === "object" &&
                    obj.id &&
                    typeof obj.id === "string" &&
                    obj.name &&
                    typeof obj.name === "string" &&
                    obj.description &&
                    typeof obj.description === "string" &&
                    obj.category &&
                    typeof obj.category === "string" &&
                    obj.execute &&
                    typeof obj.execute === "function" &&
                    obj.definition &&
                    obj.icon
                  );
                };

                let tool = null;

                if (
                  module.default &&
                  hasRequiredToolProperties(module.default)
                ) {
                  tool = module.default;
                } else {
                  for (const key of Object.keys(module)) {
                    if (hasRequiredToolProperties(module[key])) {
                      tool = module[key];
                      break;
                    }
                  }
                }

                if (tool) {
                  try {
                    this.register(tool, { skipDuplicates: true });

                    return tool;
                  } catch (registerError) {
                    console.error(
                      `Error registering ${id} from inferred path:`,
                      registerError.message
                    );
                  }
                }
              } catch (inferError) {
                // Silently ignore inference errors and continue with other registration attempts
              }
            }
          }
        }
        return null;
      }

      let module;
      try {
        module = await import(/* @vite-ignore */ importPath);
      } catch (importError) {
        console.error(
          `Failed to import module from ${importPath}:`,
          importError.message
        );
        return null;
      }

      const hasRequiredToolProperties = (obj) => {
        return (
          obj &&
          typeof obj === "object" &&
          obj.id &&
          typeof obj.id === "string" &&
          obj.name &&
          typeof obj.name === "string" &&
          obj.description &&
          typeof obj.description === "string" &&
          obj.category &&
          typeof obj.category === "string" &&
          obj.execute &&
          typeof obj.execute === "function" &&
          obj.definition &&
          obj.icon
        );
      };

      let tool = null;

      if (module.default && hasRequiredToolProperties(module.default)) {
        tool = module.default;
      } else if (module[id] && hasRequiredToolProperties(module[id])) {
        tool = module[id];
      } else if (
        module[`${id}Tool`] &&
        hasRequiredToolProperties(module[`${id}Tool`])
      ) {
        tool = module[`${id}Tool`];
      } else {
        for (const key of Object.keys(module)) {
          if (
            key !== "default" &&
            hasRequiredToolProperties(module[key]) &&
            module[key].id === id
          ) {
            tool = module[key];
            break;
          }
        }
      }

      if (!tool) {
        console.error(
          `Tool ${id} import succeeded but no valid tool found in module`
        );

        return null;
      }

      try {
        this.register(tool, { skipDuplicates: true });

        return tool;
      } catch (registerError) {
        console.error(`Error registering tool ${id}:`, registerError.message);
        return null;
      }
    } catch (error) {
      console.error(`Error dynamically loading tool ${id}:`, error);
      return null;
    }
  }

  on(event, callback) {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, new Set());
    }
    this.eventListeners.get(event).add(callback);
    return this;
  }

  off(event, callback) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.delete(callback);
    }
    return this;
  }

  emit(event, data) {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach((callback) => callback(data));
    }
  }

  getStats() {
    return {
      totalTools: this.tools.size,
      categories: Array.from(this.categories),
      toolsByCategory: Object.fromEntries(
        Array.from(this.categories).map((category) => [
          category,
          this.getToolsByCategory(category).length,
        ])
      ),
    };
  }
}

export const toolRegistry = new ToolRegistry();

export const TOOL_REGISTRY = {
  web_search: toolRegistry.getTool("web_search", false),
};

export default toolRegistry;
